import React, { useEffect, useState } from 'react';
import API from '../../services/api.js';

const AIProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testStatus, setTestStatus] = useState({});
  const [testLoading, setTestLoading] = useState({});

  const loadProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.get('/ai-providers/');
      const data = response?.data?.providers || [];
      const normalized = data.map((p) => ({
        ...p,
        _newKey: '',
        _clearKey: false,
      }));
      setProviders(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const updateProvider = (key, changes) => {
    setProviders((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...changes } : p))
    );
    if (Object.prototype.hasOwnProperty.call(changes, '_newKey')) {
      setTestStatus((prev) => ({ ...prev, [key]: null }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates = providers.map((p) => ({
        key: p.key,
        enabled: p.enabled,
        api_key: p._newKey || undefined,
        clear_key: p._clearKey || false,
      }));
      await API.post('/ai-providers/', { providers: updates });
      await loadProviders();
    } catch (err) {
      setError(err.message || 'Failed to save providers');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTestLoading((prev) => ({ ...prev, [provider.key]: true }));
    setTestStatus((prev) => ({ ...prev, [provider.key]: null }));
    try {
      const payload = {
        provider_key: provider.key,
      };
      if (provider._newKey) {
        payload.api_key = provider._newKey;
      }
      const response = await API.post('/ai-providers/test/', payload);
      const ok = response?.data?.success === true;
      setTestStatus((prev) => ({
        ...prev,
        [provider.key]: {
          success: ok,
          message: response?.data?.message || (ok ? 'API key is valid' : 'API key test failed'),
        },
      }));
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message || 'API key test failed';
      setTestStatus((prev) => ({
        ...prev,
        [provider.key]: { success: false, message },
      }));
    } finally {
      setTestLoading((prev) => ({ ...prev, [provider.key]: false }));
    }
  };

  const isSaveBlocked = () => {
    return providers.some((p) => p._newKey && testStatus[p.key]?.success !== true);
  };

  return (
    <div className="text-white p-6 m-12">
      <style>{`
        @keyframes aiProviderTestSweep {
          0% { transform: translateX(-100%); opacity: 0.2; }
          50% { opacity: 0.9; }
          100% { transform: translateX(200%); opacity: 0.2; }
        }
        .ai-provider-test-bar {
          position: relative;
          height: 3px;
          overflow: hidden;
          background: rgba(59, 130, 246, 0.12);
          border-radius: 999px;
        }
        .ai-provider-test-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 40%;
          background: linear-gradient(90deg, transparent, rgba(59,130,246,0.9), transparent);
          animation: aiProviderTestSweep 1.2s linear infinite;
        }
      `}</style>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">AI Providers</h2>
          <p className="text-gray-400 text-sm mt-1">Configure provider API keys and availability for the platform.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || isSaveBlocked()}
          className={`px-4 py-2 rounded-xl border transition-all ${
            saving || isSaveBlocked()
              ? 'bg-gray-700 border-gray-500 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600/20 border-blue-400 text-blue-400 hover:bg-blue-600/30'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      {isSaveBlocked() && (
        <p className="text-xs text-amber-400 -mt-2 mb-4">
          Test all new API keys before saving.
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-400">Loading providers...</p>
      ) : (
        <div className="space-y-4">
          {providers.map((provider) => (
            <div key={provider.key} className="border border-[#232325] rounded-xl bg-[#101012] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">{provider.label}</p>
                  <p className="text-gray-400 text-sm">Status: {provider.enabled ? 'Enabled' : 'Disabled'}</p>
                  <p className="text-gray-500 text-xs">Key: {provider.masked_key || 'Not set'}</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!provider.enabled}
                    onChange={(e) => updateProvider(provider.key, { enabled: e.target.checked })}
                    disabled={!provider.has_key && !provider._newKey}
                  />
                  Enable
                </label>
              </div>
              {!provider.has_key && !provider._newKey && (
                <p className="text-xs text-amber-400 mt-2">
                  Add an API key before enabling this provider.
                </p>
              )}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">API Key</label>
                  <input
                    type="password"
                    value={provider._newKey}
                    onChange={(e) => updateProvider(provider.key, { _newKey: e.target.value, _clearKey: false })}
                    placeholder="Enter new API key"
                    className="w-full text-white border border-[#232325] bg-[#0D0D0E] p-2 rounded-xl"
                  />
                  {provider._newKey && !testStatus[provider.key] && (
                    <p className="text-xs text-amber-400 mt-2">
                      Test this key before saving.
                    </p>
                  )}
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => updateProvider(provider.key, { _newKey: '', _clearKey: true })}
                    className="px-3 py-2 rounded-xl border border-red-500/40 text-red-400 bg-red-500/10 hover:bg-red-500/20"
                  >
                    Clear Key
                  </button>
                  <button
                    onClick={() => handleTest(provider)}
                    disabled={testLoading[provider.key]}
                    className={`px-3 py-2 rounded-xl border ${testLoading[provider.key] ? 'bg-gray-700 border-gray-500 text-gray-400' : 'bg-emerald-600/20 border-emerald-400 text-emerald-400 hover:bg-emerald-600/30'}`}
                  >
                    {testLoading[provider.key] ? 'Testing...' : 'Test Key'}
                  </button>
                </div>
              </div>
              {testStatus[provider.key] && (
                <div className={`mt-3 p-3 rounded-lg border ${testStatus[provider.key].success ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-red-500/40 bg-red-500/10 text-red-300'}`}>
                  {testStatus[provider.key].message}
                </div>
              )}
              {testLoading[provider.key] && (
                <div className="mt-3 ai-provider-test-bar" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIProviders;
